import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DeleteCriterionModalState,
  renderSectionTitle
} from "@/components/controls/Questionnaires/POCQuestionnaire";
import { useCreateQuestionMutation } from "@/pages/deals/hooks";
import { useQueryClient } from "@tanstack/react-query";
import {
  CreateQuestionnaireQuestionSettings,
  DealQuestionnaireQuestionSettings,
  DealsQuestionnaireData,
  QuestionResponse,
  QuestionValueType,
  Responses,
  Score
} from "@/pages/deals/types";
import { Add } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import YesOrNoQuestion from "@/components/controls/Questions/YesOrNoQuestion";
import Text from "@/components/ui/text";
import { emptyQuestion } from "@/pages/deals/helpers";
import { filter, map, sortBy } from "lodash";
import SuccessCriteriaEditMode from "@/components/controls/SuccessCriteria/SuccessCriteriaEditMode";

export interface EditingState {
  questionId: string;
  value: string;
}

interface Props {
  dealId: string;
  questionnaireId: string;
  questionnaire: DealsQuestionnaireData;
  questions: DealQuestionnaireQuestionSettings[] | null;
  setQuestions: React.Dispatch<
    React.SetStateAction<DealQuestionnaireQuestionSettings[] | null>
  >;
  localResponses: Responses;
  updateResponse: (
    questionId: string,
    response: QuestionResponse | null
  ) => void;
  resetResponses: () => void;
  setDeleteCriterionModalState: React.Dispatch<
    React.SetStateAction<DeleteCriterionModalState>
  >;
  handleUpdateQuestionnaireScore: (newValue: Score | null) => void;
  handleValueChange: (value: QuestionValueType, questionId: string) => void;
}

const SuccessCriteria: React.FC<Props> = ({
  dealId,
  questionnaireId,
  questionnaire,
  questions,
  setQuestions,
  localResponses,
  updateResponse,
  resetResponses,
  setDeleteCriterionModalState,
  handleUpdateQuestionnaireScore,
  handleValueChange
}) => {
  const { t } = useTranslation();

  const createQuestion = useCreateQuestionMutation();
  const queryClient = useQueryClient();

  const [isEditMode, setIsEditMode] = useState(true);
  const [editingState, setEditingState] = useState<EditingState | null>(null);

  const checkboxQuestions = sortBy(
    filter(
      questions,
      (question) =>
        (question.responseType.type === "checkbox" &&
          question.isSuccessCriteria) ??
        false
    ),
    "questionOrder"
  );

  const handleAddCustomCheckbox = () => {
    const newQuestion: CreateQuestionnaireQuestionSettings = {
      ...emptyQuestion,
      questionnaireId,
      dealId,
      responseType: {
        type: "checkbox",
        setValueType: null,
        unit: null,
        value: "bool"
      }
    };
    createQuestion.mutate(
      {
        data: newQuestion
      },
      {
        onSuccess: (data) => {
          setQuestions((prevQuestions) => [
            ...(prevQuestions || []),
            { ...newQuestion, ...data }
          ]);
          setEditingState({
            questionId: data.id,
            value: ""
          });
          queryClient.invalidateQueries({
            queryKey: ["settings/questionnaireSettings"]
          });
        }
      }
    );
  };

  const renderBooleanQuestions = () => {
    const booleanQuestions = filter(questions, (question) => {
      return (
        (question.responseType?.type === "bool" &&
          question.isSuccessCriteria) ??
        false
      );
    });

    return map(booleanQuestions, (question) => (
      <YesOrNoQuestion
        key={question.id}
        question={question}
        questionnaireId={questionnaireId}
        dealId={dealId}
        handleUpdateQuestionnaireScore={handleUpdateQuestionnaireScore}
        localResponses={localResponses}
        updateResponse={updateResponse}
        onResponseError={resetResponses}
        className="gap-6 justify-normal"
      />
    ));
  };

  return (
    <div className="flex flex-col gap-8 flex-1 px-8">
      <div className="flex flex-col gap-1">
        <div className="flex w-full justify-between">
          {renderSectionTitle(t, questionnaire?.name, "successCriteria")}
          {!isEditMode && (
            <Button variant="secondaryNoBg" onClick={() => setIsEditMode(true)}>
              {t("deals.evaluationQuestionnaire.successCriteria.edit")}
            </Button>
          )}
          {isEditMode && (
            <Button
              variant="tertiary"
              onClick={handleAddCustomCheckbox}
              className="w-fit"
            >
              <Add />
              {t("deals.evaluationQuestionnaire.successCriteria.addCustom")}
            </Button>
          )}
        </div>
        <Text variant="bodySecondary">
          {t(
            `deals.evaluationQuestionnaire.successCriteria.${isEditMode ? "editModeSubtitle" : "subtitle"}`
          )}
        </Text>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          {isEditMode ? (
            <SuccessCriteriaEditMode
              dealId={dealId}
              questionnaireId={questionnaireId}
              questions={questions}
              setQuestions={setQuestions}
              localResponses={localResponses}
              updateResponse={updateResponse}
              setDeleteCriterionModalState={setDeleteCriterionModalState}
              handleValueChange={handleValueChange}
              editingState={editingState}
              setEditingState={setEditingState}
              onAddNewRow={handleAddCustomCheckbox}
              resetResponses={resetResponses}
            />
          ) : (
            map(checkboxQuestions, (question) => (
              <YesOrNoQuestion
                key={question.id}
                question={question}
                questionnaireId={questionnaireId}
                dealId={dealId}
                handleUpdateQuestionnaireScore={handleUpdateQuestionnaireScore}
                localResponses={localResponses}
                updateResponse={updateResponse}
                onResponseError={resetResponses}
                options={[t("pass"), t("fail")]}
                hideMandatorySign
              />
            ))
          )}
        </div>
        <div className="flex flex-col gap-2">{renderBooleanQuestions()}</div>
      </div>
    </div>
  );
};

export default SuccessCriteria;
